import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, signal, inject } from "@angular/core";
import {
    ChangePasswordDto,
    ForgetPasswordDto,
    ResetPasswordDto,
    EmailVerificationDto,
} from "src/app/core/dto/user-auth/password.dto";
import {
    DeleteUserDto,
    InvitedUserSignupDto,
    RegisterUserDto,
    UpdateUserDto,
    UserLoginDto,
} from "src/app/core/dto/user-auth/auth-request.dto";
import { RegistrationResponse } from "src/app/core/dto/user-auth/auth-response.dto";
import { UserDto } from "src/app/core/dto/user-auth/user.dto";
import { EmailVerificationResponse } from "src/app/core/dto/user-auth/email-verification-response.dto";
import { LoginAccessTokenDto } from "src/app/core/dto/user-auth/token.dto";
import { SuccessDto } from "src/app/core/dto/shared/api-response.dto";
import { ReplaySubject, catchError, finalize, of, switchMap } from "rxjs";
import { Observable } from "rxjs";
import { SKIP_ERROR_TOAST } from "src/app/core/interceptors/http-context-tokens";

export enum UserPermission {
    SuperAdmin = 1,
    Admin = 2,
    SiteManager = 3,
    Demo = 4,
}

@Injectable({
    providedIn: "root",
})
export class UserAuthService {
    private readonly http = inject(HttpClient);
    readonly userPopulated$ = new ReplaySubject<boolean>(1);
    readonly user = signal<UserDto | undefined>(undefined);

    constructor() {
        if (
            localStorage.getItem("access_token") ||
            sessionStorage.getItem("access_token")
        ) {
            this.getUser().subscribe();
        } else {
            this.userPopulated$.next(true);
        }
    }

    getUser() {
        return this.http
            .get<UserDto>("/users/me", {
                context: new HttpContext().set(SKIP_ERROR_TOAST, true),
            })
            .pipe(
                switchMap((user) => {
                    this.user.set(user);
                    this.userPopulated$.next(true);
                    return of(user);
                }),
                catchError(() => {
                    this.userPopulated$.next(true);
                    return of(undefined);
                }),
                finalize(() => {
                    this.userPopulated$.next(true);
                }),
            );
    }

    emptyUser() {
        this.user.set(undefined);
        this.userPopulated$.next(true);
    }

    login(email: string, password: string) {
        return this.http
            .post<LoginAccessTokenDto>("/auth/signin", { email, password })
            .pipe(switchMap((token) => of(token)));
    }

    adminRegister(dto: RegisterUserDto) {
        return this.http
            .post<LoginAccessTokenDto>("/user/admin-create", dto)
            .pipe(switchMap((token) => of(token)));
    }

    register(dto: InvitedUserSignupDto): Observable<RegistrationResponse> {
        return this.http.post<RegistrationResponse>(`/users`, dto);
    }

    forgetPassword(dto: ForgetPasswordDto) {
        return this.http.post<SuccessDto>("/auth/forget-password", dto, {
            context: new HttpContext().set(SKIP_ERROR_TOAST, true),
        });
    }

    getPasswordResetExpirationTime(userId: string) {
        return this.http.get<SuccessDto>("/user/get-reset-password-time", {
            params: { userId },
        });
    }

    logout() {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        sessionStorage.clear();
        this.user.set(undefined);
        this.userPopulated$.next(true);
    }

    refreshToken() {
        return this.http
            .post<LoginAccessTokenDto>("/user/refresh-token", {})
            .pipe(switchMap((token) => of(token)));
    }

    loginViaUsernamePassword(body: UserLoginDto) {
        return this.http
            .post<LoginAccessTokenDto>("/auth/signin", body)
            .pipe(switchMap((token) => of(token)));
    }

    updateUser(userId: number, user: UpdateUserDto): Observable<SuccessDto> {
        return this.http.put<SuccessDto>(`/users?id=${userId}`, user);
    }

    changePassword(user: ChangePasswordDto): Observable<SuccessDto> {
        return this.http.post<SuccessDto>("/auth/change-password", user);
    }

    resetPassword(user: ResetPasswordDto) {
        return this.http.post<SuccessDto>("/auth/reset-password", user);
    }

    verifyEmail(token: string) {
        const dto = new EmailVerificationDto();
        dto.token = token;
        return this.http.put<EmailVerificationResponse>(
            "/auth/verify-email",
            dto,
        );
    }

    updateImage(form: FormData) {
        return this.http.put<SuccessDto>("/user/change-image", form);
    }

    deleteUser(dto: DeleteUserDto) {
        return this.http.delete<SuccessDto>("/user", {
            params: { userId: dto.userId },
        });
    }

    hasPermission(permission: UserPermission) {
        return (
            this.user()?.userRoles.some((role) => role.id === permission) ??
            false
        );
    }

    getToken() {
        return (
            localStorage.getItem("access_token") ||
            sessionStorage.getItem("access_token")
        );
    }
}
